import { createSelector, createSelectorCreator, defaultMemoize } from "reselect";
import { defaultCamerasProfileWidgets } from "orion-components/Profiles/Utils/CameraProfileWidgets";
import { defaultFacilityProfileWidgets } from "orion-components/Profiles/Utils/FacilityProfileWidgets";
import { defaultEntityProfileWidgets } from "orion-components/Profiles/Utils/EntityProfileWidgets";
import { defaultDeviceProfileWidgets } from "orion-components/Profiles/Utils/DeviceProfileWidgets";
import { defaultEventProfileWidgets } from "orion-components/Profiles/Utils/EventProfileWidgets";
import { defaultRobotProfileWidgets } from "orion-components/Profiles/Utils/RobotProfileWidgets";
import { defaultGISProfileWidgets } from "orion-components/Profiles/Utils/GISProfileWidgets";
import { selectedContextSelector } from "orion-components/ContextPanel/Selectors";
import isEqual from "lodash/isEqual";

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

// widget state
const entityTypeSelector = state => state.appState?.contextPanel?.profile?.selectedEntity?.type || null;
const profileWidgetOrderSelector = state => state.appState?.persisted?.profiles || null;

const EMPTY_ARRAY = [];

const getDefaultProfileWidgets = entityType => {
	switch (entityType) {
		case "camera":
			return defaultCamerasProfileWidgets;
		case "device":
			return defaultDeviceProfileWidgets;
		case "facility":
			return defaultFacilityProfileWidgets;
		case "entity":
			return defaultEntityProfileWidgets;
		case "event":
			return defaultEventProfileWidgets;
		case "robot":
			return defaultRobotProfileWidgets;
		case "gis":
			return defaultGISProfileWidgets;
		default:
			return EMPTY_ARRAY;
	}
};

const getWidgetState = createDeepEqualSelector(
	[entityTypeSelector, profileWidgetOrderSelector, selectedContextSelector],
	(entityTypeState, widgetOrder, context) => (id, widgetData) => {
		let entityType = entityTypeState;
		const isRobotTrack = context?.entity?.entityData?.properties?.type === "Robot Track";

		if (entityTypeState === "track" || entityTypeState === "shapes") {
			entityType = isRobotTrack ? "robot" : "entity";
		}

		if (widgetOrder === null || !widgetOrder[entityType]?.widgetOrder) {
			const widget = getDefaultProfileWidgets(entityType).find(widget => widget.id === id);
			return widget ? widget[widgetData] : null;
		}

		if (entityType in widgetOrder) {
			const widget = widgetOrder[entityType]?.widgetOrder.find(widget => widget.id === id);
			return widget ? widget[widgetData] : null;
		} else {
			if (widgetData === "enabled") {
				return false;
			}

			return null;
		}
	}
);
// widget state

//permissions

const selectIntegrations = state => state.session?.user?.profile?.integrations || [];

const checkPermissionBasedOnFeedId = createSelector(
	[selectIntegrations],
	integrations =>
		(feedId, type, permission = "") => {
			const integration = integrations?.find(int => int.intId === feedId);
			if (type === "permissions") {
				return integration?.permissions?.includes(permission);
			} else {
				return integration?.config?.canView;
			}
		}
);

//permissions
const applicationState = (state, appId, permission, type) => {
	const applications = state.session?.user?.profile?.applications || [];
	const application = applications?.find(app => app.appId === appId);
	if (type === "config") {
		return application?.config[permission];
	} else {
		return application?.permissions?.includes(permission);
	}
};

const canManageByApplication = createSelector([applicationState], canManage => {
	return canManage;
});

// widget expandable and launchable
const getAppId = state => state?.application?.appId;

export const contextualDataState = state => state.contextualData;

const getSelectedContextData = createDeepEqualSelector([contextualDataState], context => (contextId, extract = null) => {
	if (context && context[contextId]) {
		const contextData = context[contextId];
		if (extract === null) {
			return contextData;
		} else {
			return contextData[extract];
		}
	} else {
		return EMPTY_ARRAY;
	}
});

export const selectSelectedContextData = createSelector(
	[contextualDataState, (_, contextId) => contextId, (_, __, extract) => extract],
	(context, contextId, extract) => {
		if (!context || !context[contextId]) {
			return EMPTY_ARRAY;
		}

		const contextData = context[contextId];

		return extract == null ? contextData : contextData[extract];
	}
);

export {
	getWidgetState,
	checkPermissionBasedOnFeedId,
	canManageByApplication,
	getAppId,
	entityTypeSelector,
	getSelectedContextData
};
